/**
 * Tiny fetch wrapper around the Lighthouse API.
 *
 * Base URL is read from VITE_API_URL (set at build time). When DNS
 * lands we'll switch to api.lighthouseapp.net; for now it points at
 * the API Gateway invoke URL.
 */

const BASE = import.meta.env.VITE_API_URL || "https://0gkyaupw13.execute-api.eu-west-2.amazonaws.com";

export type Status = "operational" | "degraded" | "offline" | "maintenance";
export type StatusBadge = "ok" | "warn" | "down" | "maint" | "unknown";

export const statusBadge = (s?: string): StatusBadge => {
  switch (s) {
    case "operational": return "ok";
    case "degraded":    return "warn";
    case "offline":     return "down";
    case "maintenance": return "maint";
    default:            return "unknown";
  }
};

export const statusLabel = (s?: string) => s ? s[0].toUpperCase() + s.slice(1) : "Unknown";

export type Service = {
  service_id: string;
  name?: string;
  category?: string;
  ip?: string;
  unit_id?: string;
  scope?: "building" | "unit";
  latest_status?: Status;
  latest_latency_ms?: number | null;
  latest_ts?: number;
};

export type Unit = {
  id: string;
  name: string;
  short?: string;
  ip_router?: string;
};

export type EstateSummary = {
  id: string;
  profile: { name: string; address?: string; type?: string };
  services: Service[];
};

export type Room = {
  room_id: string;
  name: string;
  type: "Building" | "Room" | "DistributionBoard" | string;
  parent_id?: string;
  unit_id?: string;
};

export type Device = {
  device_id: string;
  name: string;
  address: string;  // KNX individual address e.g. "1.2.34"
  room_id?: string;
  unit_id?: string;
  product_ref?: string;
  /** ms-epoch of the most recent telegram the device sent on the bus.
   *  Updated by knx-presence.py on knxmonitor1 every 5 minutes. */
  last_telegram_ts?: number;
};

export type EstateDetailDoc = {
  id: string;
  profile: any;
  units: Unit[];
  services: Service[];
  rooms?: Room[];
  devices?: Device[];
  status: Record<string, { ts: number; status: Status; latency_ms?: number }[]>;
};

/** Authed fetch — picks up Authorization from auth.tsx token storage. */
async function authedFetch(path: string): Promise<any> {
  const tok = localStorage.getItem("id_token");
  const headers: Record<string, string> = {};
  if (tok) headers["Authorization"] = `Bearer ${tok}`;
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function listEstates(): Promise<EstateSummary[]> {
  const body = await authedFetch(`/estates`);
  return body.estates;
}

export async function getEstate(id: string): Promise<EstateDetailDoc> {
  return authedFetch(`/estates/${id}`);
}
